import React, { useState } from 'react';
import { Input, Button, message, Typography, Divider } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, ReloadOutlined, BulbOutlined, QuestionCircleOutlined, UpOutlined, DownOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { TextArea } = Input;
const { Text } = Typography;

/**
 * QuizPanel - AI Quiz interface matching DashboardQA quiz functionality
 */
const QuizPanel = ({ selectedNote }) => {
  const { t } = useTranslation(['dashboard', 'messages', 'common']);

  const [hasGenerated, setHasGenerated] = useState(false);
  const [quizState, setQuizState] = useState({
    currentQuestion: null,
    userAnswer: '',
    isGenerating: false,
    isEvaluating: false,
    history: [],
    lastResult: null,
    coveragePercentage: 0,
    exhausted: false,
    exhaustedReason: '',
    quizCompleted: false,
    isLastQuestion: false,
    showSummary: false
  });

  const handleGenerateQuestion = async () => {
    if (!selectedNote?.id) {
      message.warning(t('messages:no_note_selected', { defaultValue: 'Please select a note first' }));
      return;
    }

    setQuizState(prev => ({ ...prev, isGenerating: true }));

    try {
      const baseUrl = process.env.REACT_APP_API_URL || '/api';
      const authStorage = localStorage.getItem('auth-storage');
      const token = authStorage ? JSON.parse(authStorage).state?.token : null;

      const response = await fetch(`${baseUrl}/notes/qa/quiz_generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({
          noteId: selectedNote.id,
          previousQuestions: quizState.history.map(h => h.question)
        })
      });

      if (!response.ok) {
        if (response.status === 402) {
          throw new Error('INSUFFICIENT_CREDITS');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Check if quiz is exhausted (no more questions available)
      if (result.exhausted && !result.question) {
        setQuizState(prev => ({
          ...prev,
          currentQuestion: null,
          userAnswer: '',
          isGenerating: false,
          coveragePercentage: result.coverage_percentage || 100,
          exhausted: true,
          exhaustedReason: result.reason || '',
          quizCompleted: true
        }));
        return;
      }

      setQuizState(prev => ({
        ...prev,
        currentQuestion: result.question,
        userAnswer: '',
        isGenerating: false,
        coveragePercentage: result.coverage_percentage || 0
      }));
      setHasGenerated(true);
    } catch (error) {
      console.error('[QuizPanel] Generate failed:', error);
      const msg = error.message === 'INSUFFICIENT_CREDITS'
        ? t('dashboard:insufficient_credits', { defaultValue: 'Insufficient credits. Please purchase more credits to continue.' })
        : t('messages:quiz_generate_failed', { defaultValue: 'Failed to generate question. Please try again.' });
      message.error(msg);
      setQuizState(prev => ({ ...prev, isGenerating: false }));
    }
  };

  const handleSubmitAnswer = async () => {
    if (!quizState.userAnswer.trim()) return;

    setQuizState(prev => ({ ...prev, isEvaluating: true }));

    try {
      const baseUrl = process.env.REACT_APP_API_URL || '/api';
      const authStorage = localStorage.getItem('auth-storage');
      const token = authStorage ? JSON.parse(authStorage).state?.token : null;

      const response = await fetch(`${baseUrl}/notes/qa/quiz_evaluate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({
          noteId: selectedNote.id,
          question: quizState.currentQuestion,
          userAnswer: quizState.userAnswer
        })
      });

      if (!response.ok) {
        if (response.status === 402) {
          throw new Error('INSUFFICIENT_CREDITS');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      setQuizState(prev => ({
        ...prev,
        isEvaluating: false,
        lastResult: {
          question: prev.currentQuestion,
          userAnswer: prev.userAnswer,
          isCorrect: result.isCorrect,
          feedback: result.feedback,
          correctAnswer: result.correctAnswer,
          sourceQuote: result.sourceQuote
        },
        history: [...prev.history, {
          question: prev.currentQuestion,
          userAnswer: prev.userAnswer,
          isCorrect: result.isCorrect,
          feedback: result.feedback,
          correctAnswer: result.correctAnswer,
          sourceQuote: result.sourceQuote
        }]
      }));
    } catch (error) {
      console.error('[QuizPanel] Evaluate failed:', error);
      const msg = error.message === 'INSUFFICIENT_CREDITS'
        ? t('dashboard:insufficient_credits', { defaultValue: 'Insufficient credits. Please purchase more credits to continue.' })
        : t('messages:quiz_evaluate_failed', { defaultValue: 'Failed to evaluate answer. Please try again.' });
      message.error(msg);
      setQuizState(prev => ({ ...prev, isEvaluating: false }));
    }
  };

  const handleNextQuestion = () => {
    setQuizState(prev => ({
      ...prev,
      currentQuestion: null,
      userAnswer: '',
      lastResult: null
    }));
    handleGenerateQuestion();
  };

  const handleRetake = () => {
    setQuizState({
      currentQuestion: null,
      userAnswer: '',
      isGenerating: false,
      isEvaluating: false,
      history: [],
      lastResult: null,
      coveragePercentage: 0,
      exhausted: false,
      exhaustedReason: '',
      quizCompleted: false,
      isLastQuestion: false,
      showSummary: false
    });
    setHasGenerated(false);
  };

  // Generate screen (before quiz starts)
  if (!hasGenerated && !quizState.quizCompleted) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: '#fafafa' }}>
        {/* Header */}
        <div style={{
          padding: '16px 24px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <BulbOutlined style={{ fontSize: 16, color: '#fff' }} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#111827', lineHeight: '20px', fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', WebkitFontSmoothing: 'antialiased' }}>
                {t('dashboard:ai_quiz', { defaultValue: 'AI Quiz' })}
              </h3>
              <p style={{ margin: 0, fontSize: 12, color: '#4b5563', lineHeight: '16px', fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', WebkitFontSmoothing: 'antialiased' }}>
                {t('dashboard:test_understanding', { defaultValue: 'Test your understanding' })}
              </p>
            </div>
          </div>
        </div>

        {/* Generate Screen */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          textAlign: 'center'
        }}>
          <BulbOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '16px' }} />
          <Text style={{ fontSize: '16px', color: '#666', marginBottom: '24px' }}>
            {t('dashboard:quiz_no_question', { defaultValue: "Click 'Generate Question' to start the quiz" })}
          </Text>
          <Button
            type="primary"
            size="large"
            icon={<QuestionCircleOutlined />}
            onClick={handleGenerateQuestion}
            disabled={!selectedNote?.id || quizState.isGenerating}
            loading={quizState.isGenerating}
            style={{ borderRadius: '8px' }}
          >
            {quizState.isGenerating
              ? t('dashboard:quiz_generating', { defaultValue: 'Generating...' })
              : t('dashboard:quiz_generate_question', { defaultValue: 'Generate Question' })}
          </Button>
          {!selectedNote?.id && (
            <Text type="secondary" style={{ marginTop: '12px', fontSize: '13px' }}>
              {t('messages:no_note_selected', { defaultValue: 'Please select a note first' })}
            </Text>
          )}
        </div>
      </div>
    );
  }

  // Quiz Exhausted - No more questions available
  if (quizState.exhausted && quizState.quizCompleted) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: '#fafafa' }}>
        {/* Header */}
        <div style={{
          padding: '16px 24px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <BulbOutlined style={{ fontSize: 16, color: '#fff' }} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#111827', lineHeight: '20px', fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', WebkitFontSmoothing: 'antialiased' }}>
                {t('dashboard:quiz_results', { defaultValue: 'Quiz Results' })}
              </h3>
              <p style={{ margin: 0, fontSize: 12, color: '#4b5563', lineHeight: '16px', fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', WebkitFontSmoothing: 'antialiased' }}>
                {t('dashboard:review_answers', { defaultValue: 'Review your answers' })}
              </p>
            </div>
          </div>
        </div>

        {/* Quiz Complete Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            padding: '24px',
            border: '2px solid #52c41a',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}>
            {/* Completed Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '20px',
              padding: '16px',
              backgroundColor: '#f6ffed',
              borderRadius: '8px'
            }}>
              <CheckCircleOutlined style={{ fontSize: '32px', color: '#52c41a', marginRight: '12px' }} />
              <div style={{ textAlign: 'center' }}>
                <Text strong style={{ fontSize: '18px', color: '#52c41a', display: 'block' }}>
                  {t('dashboard:quiz_exhausted_title', { defaultValue: 'Quiz Complete!' })}
                </Text>
                <Text type="secondary">
                  {t('dashboard:quiz_exhausted_subtitle', { defaultValue: 'No more questions available for this note.' })}
                </Text>
              </div>
            </div>

            {/* Exhausted Reason */}
            {quizState.exhaustedReason && (
              <div style={{
                marginBottom: '20px',
                padding: '12px',
                backgroundColor: '#fffbe6',
                borderRadius: '6px',
                borderLeft: '3px solid #faad14'
              }}>
                <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                  {t('dashboard:quiz_exhausted_reason_label', { defaultValue: 'Reason:' })}
                </Text>
                <Text style={{ fontSize: '14px' }}>
                  {quizState.exhaustedReason}
                </Text>
              </div>
            )}

            {/* Stats Summary */}
            {quizState.history.length > 0 && (
              <>
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '24px',
                  marginBottom: '20px',
                  padding: '16px',
                  backgroundColor: '#fafafa',
                  borderRadius: '8px'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <Text style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                      {quizState.history.length}
                    </Text>
                    <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>
                      {t('dashboard:quiz_total_questions', { defaultValue: 'Total' })}
                    </Text>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <Text style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                      {quizState.history.filter(h => h.isCorrect).length}
                    </Text>
                    <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>
                      {t('dashboard:quiz_correct_answers', { defaultValue: 'Correct' })}
                    </Text>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <Text style={{ fontSize: '24px', fontWeight: 'bold', color: '#ff4d4f' }}>
                      {quizState.history.filter(h => !h.isCorrect).length}
                    </Text>
                    <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>
                      {t('dashboard:quiz_incorrect_answers', { defaultValue: 'Incorrect' })}
                    </Text>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <Text style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                      {quizState.history.length > 0
                        ? Math.round((quizState.history.filter(h => h.isCorrect).length / quizState.history.length) * 100)
                        : 0}%
                    </Text>
                    <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>
                      {t('dashboard:quiz_accuracy', { defaultValue: 'Accuracy' })}
                    </Text>
                  </div>
                </div>

                {/* View Summary Button */}
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                  <Button
                    type="primary"
                    icon={quizState.showSummary ? <UpOutlined /> : <DownOutlined />}
                    onClick={() => {
                      setQuizState(prev => ({
                        ...prev,
                        showSummary: !prev.showSummary
                      }));
                    }}
                    style={{ borderRadius: '6px' }}
                  >
                    {quizState.showSummary
                      ? t('dashboard:quiz_hide_summary', { defaultValue: 'Hide Summary' })
                      : t('dashboard:quiz_view_summary', { defaultValue: 'View Quiz Summary' })}
                  </Button>
                </div>

                {/* Quiz Summary - All Q&A History */}
                {quizState.showSummary && (
                  <div style={{ marginTop: '16px' }}>
                    <Divider style={{ margin: '16px 0' }} />
                    <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: '16px' }}>
                      {t('dashboard:quiz_history_title', { defaultValue: 'Questions & Answers History' })}
                    </Text>

                    {quizState.history.map((item, index) => (
                      <div
                        key={index}
                        style={{
                          marginBottom: '16px',
                          padding: '16px',
                          borderRadius: '8px',
                          border: `2px solid ${item.isCorrect ? '#52c41a' : '#ff4d4f'}`,
                          backgroundColor: item.isCorrect ? '#f6ffed' : '#fff2f0'
                        }}
                      >
                        {/* Result Header with Question Number */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: '12px'
                        }}>
                          {item.isCorrect ? (
                            <CheckCircleOutlined style={{ fontSize: '24px', color: '#52c41a', marginRight: '12px' }} />
                          ) : (
                            <CloseCircleOutlined style={{ fontSize: '24px', color: '#ff4d4f', marginRight: '12px' }} />
                          )}
                          <Text strong style={{
                            fontSize: '16px',
                            color: item.isCorrect ? '#52c41a' : '#ff4d4f'
                          }}>
                            Q{index + 1}: {item.isCorrect
                              ? t('dashboard:quiz_correct', { defaultValue: 'Correct!' })
                              : t('dashboard:quiz_incorrect', { defaultValue: 'Incorrect' })}
                          </Text>
                        </div>

                        {/* Question */}
                        <div style={{
                          marginBottom: '12px',
                          padding: '12px',
                          backgroundColor: '#fff',
                          borderRadius: '6px'
                        }}>
                          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                            {t('dashboard:quiz_question_label', { defaultValue: 'Question:' })}
                          </Text>
                          <Text style={{ fontSize: '14px', lineHeight: '1.6' }}>
                            {item.question}
                          </Text>
                        </div>

                        {/* Your Answer */}
                        <div style={{
                          marginBottom: '12px',
                          padding: '12px',
                          backgroundColor: '#fff',
                          borderRadius: '6px'
                        }}>
                          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                            {t('dashboard:quiz_your_answer_label', { defaultValue: 'Your Answer:' })}
                          </Text>
                          <Text style={{ fontSize: '14px', lineHeight: '1.6' }}>
                            {item.userAnswer}
                          </Text>
                        </div>

                        {/* Feedback */}
                        {item.feedback && (
                          <div style={{
                            marginBottom: '12px',
                            padding: '12px',
                            backgroundColor: '#fff',
                            borderRadius: '6px'
                          }}>
                            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                              {t('dashboard:quiz_feedback_label', { defaultValue: 'Feedback:' })}
                            </Text>
                            <Text style={{ fontSize: '14px', lineHeight: '1.6' }}>
                              {item.feedback}
                            </Text>
                          </div>
                        )}

                        {/* Correct Answer (if wrong) */}
                        {!item.isCorrect && item.correctAnswer && (
                          <div style={{
                            marginBottom: '12px',
                            padding: '12px',
                            backgroundColor: '#e6f7ff',
                            borderRadius: '6px',
                            borderLeft: '3px solid #1890ff'
                          }}>
                            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                              {t('dashboard:quiz_correct_answer_label', { defaultValue: 'Correct Answer:' })}
                            </Text>
                            <Text style={{ fontSize: '14px', lineHeight: '1.6' }}>
                              {item.correctAnswer}
                            </Text>
                          </div>
                        )}

                        {/* Source Quote */}
                        {item.sourceQuote && (
                          <div style={{
                            padding: '12px',
                            backgroundColor: '#fffbe6',
                            borderRadius: '6px',
                            borderLeft: '3px solid #faad14'
                          }}>
                            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                              {t('dashboard:quiz_source_quote_label', { defaultValue: 'Source from Note:' })}
                            </Text>
                            <Text italic style={{ fontSize: '13px', lineHeight: '1.6', color: '#666' }}>
                              "{item.sourceQuote}"
                            </Text>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Start New Quiz Button */}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleRetake}
                style={{ borderRadius: '6px' }}
              >
                {t('dashboard:quiz_restart', { defaultValue: 'Start New Quiz' })}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Generating state
  if (quizState.isGenerating) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: '#fafafa' }}>
        {/* Header */}
        <div style={{
          padding: '16px 24px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <BulbOutlined style={{ fontSize: 16, color: '#fff' }} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#111827', lineHeight: '20px', fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', WebkitFontSmoothing: 'antialiased' }}>
                {t('dashboard:ai_quiz', { defaultValue: 'AI Quiz' })}
              </h3>
              <p style={{ margin: 0, fontSize: 12, color: '#4b5563', lineHeight: '16px', fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', WebkitFontSmoothing: 'antialiased' }}>
                {t('dashboard:test_understanding', { defaultValue: 'Test your understanding' })}
              </p>
            </div>
          </div>
        </div>

        {/* Generating Content */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 20px'
        }}>
          <span style={{ color: '#faad14', fontSize: '32px', display: 'inline-block', animation: 'spin 2s linear infinite' }}>✦</span>
          <Text style={{ marginTop: '16px', color: '#666' }}>
            {t('dashboard:quiz_generating', { defaultValue: 'Generating question...' })}
          </Text>
          <style>{`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  // Quiz in progress - Current question display
  const currentQuestionNumber = quizState.history.length + 1;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: '#fafafa' }}>
      {/* Header */}
      <div style={{
        padding: '16px 24px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        gap: 12
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <BulbOutlined style={{ fontSize: 16, color: '#fff' }} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#111827', lineHeight: '20px', fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', WebkitFontSmoothing: 'antialiased' }}>
              {t('dashboard:ai_quiz', { defaultValue: 'AI Quiz' })}
            </h3>
            <p style={{ margin: 0, fontSize: 12, color: '#4b5563', lineHeight: '16px', fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', WebkitFontSmoothing: 'antialiased' }}>
              {t('dashboard:question_number', { defaultValue: 'Question {{number}}', number: currentQuestionNumber })}
            </p>
          </div>
        </div>
      </div>

      {/* Quiz Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
        {/* Current question display with answer input */}
        {quizState.currentQuestion && (
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '16px',
            border: '1px solid #e8e8e8',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}>
            {/* Question */}
            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '16px' }}>
              <QuestionCircleOutlined style={{ fontSize: '20px', color: '#1890ff', marginRight: '12px', marginTop: '2px' }} />
              <Text style={{ fontSize: '15px', lineHeight: '1.6', flex: 1 }}>
                {quizState.currentQuestion}
              </Text>
            </div>

            {/* Answer Input */}
            <TextArea
              placeholder={t('dashboard:quiz_your_answer', { defaultValue: 'Your answer...' })}
              value={quizState.userAnswer}
              onChange={(e) => setQuizState(prev => ({ ...prev, userAnswer: e.target.value }))}
              autoSize={{ minRows: 3, maxRows: 6 }}
              disabled={quizState.isEvaluating || quizState.lastResult}
              style={{
                marginBottom: '12px',
                borderRadius: '6px',
                backgroundColor: quizState.lastResult ? '#fafafa' : '#fff'
              }}
            />

            {/* Submit Button - only show when no result yet */}
            {!quizState.lastResult && (
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="primary"
                  onClick={handleSubmitAnswer}
                  disabled={!quizState.userAnswer.trim() || quizState.isEvaluating}
                  loading={quizState.isEvaluating}
                >
                  {t('dashboard:quiz_submit_answer', { defaultValue: 'Submit Answer' })}
                </Button>
              </div>
            )}

            {/* Quiz Result Display - shown below the question input */}
            {quizState.lastResult && (
              <div style={{
                marginTop: '16px',
                padding: '16px',
                borderRadius: '8px',
                border: `2px solid ${quizState.lastResult.isCorrect ? '#52c41a' : '#ff4d4f'}`,
                backgroundColor: quizState.lastResult.isCorrect ? '#f6ffed' : '#fff2f0'
              }}>
                {/* Result Header */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '12px'
                }}>
                  {quizState.lastResult.isCorrect ? (
                    <CheckCircleOutlined style={{ fontSize: '24px', color: '#52c41a', marginRight: '12px' }} />
                  ) : (
                    <CloseCircleOutlined style={{ fontSize: '24px', color: '#ff4d4f', marginRight: '12px' }} />
                  )}
                  <Text strong style={{
                    fontSize: '16px',
                    color: quizState.lastResult.isCorrect ? '#52c41a' : '#ff4d4f'
                  }}>
                    {quizState.lastResult.isCorrect
                      ? t('dashboard:quiz_correct', { defaultValue: 'Correct!' })
                      : t('dashboard:quiz_incorrect', { defaultValue: 'Incorrect' })}
                  </Text>
                </div>

                {/* Feedback */}
                {quizState.lastResult.feedback && (
                  <div style={{
                    marginBottom: '12px',
                    padding: '12px',
                    backgroundColor: '#fff',
                    borderRadius: '6px'
                  }}>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                      {t('dashboard:quiz_feedback_label', { defaultValue: 'Feedback:' })}
                    </Text>
                    <Text style={{ fontSize: '14px', lineHeight: '1.6' }}>
                      {quizState.lastResult.feedback}
                    </Text>
                  </div>
                )}

                {/* Correct Answer (if wrong) */}
                {!quizState.lastResult.isCorrect && quizState.lastResult.correctAnswer && (
                  <div style={{
                    marginBottom: '12px',
                    padding: '12px',
                    backgroundColor: '#e6f7ff',
                    borderRadius: '6px',
                    borderLeft: '3px solid #1890ff'
                  }}>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                      {t('dashboard:quiz_correct_answer_label', { defaultValue: 'Correct Answer:' })}
                    </Text>
                    <Text style={{ fontSize: '14px', lineHeight: '1.6' }}>
                      {quizState.lastResult.correctAnswer}
                    </Text>
                  </div>
                )}

                {/* Source Quote */}
                {quizState.lastResult.sourceQuote && (
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#fffbe6',
                    borderRadius: '6px',
                    borderLeft: '3px solid #faad14'
                  }}>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                      {t('dashboard:quiz_source_quote_label', { defaultValue: 'Source from Note:' })}
                    </Text>
                    <Text italic style={{ fontSize: '13px', lineHeight: '1.6', color: '#666' }}>
                      "{quizState.lastResult.sourceQuote}"
                    </Text>
                  </div>
                )}
              </div>
            )}

            {/* Next Question Button - below Quiz Result Display */}
            {quizState.lastResult && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
                <Button
                  type="primary"
                  icon={<QuestionCircleOutlined />}
                  onClick={handleNextQuestion}
                  style={{ borderRadius: '6px' }}
                >
                  {t('dashboard:quiz_next_question', { defaultValue: 'Next Question' })}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizPanel;
